import { getProAdminRefundRequest, queryKeys, useAuthStore } from "@dutyon/apis";
import { Alert, Button, Card, Stack, Text, Title, TitleContainer } from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { executionStatuses, readAdminRefundDetail, statuses } from "./model";
import { notificationStatuses } from "./notification-model";
import { RefundDecisionForm } from "./RefundDecisionForm";

export function ProRefundDetailPage() {
  const actor = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const { id = "" } = useParams<{ id: string }>();
  if (!token || !actor || actor.role !== "SUPER_ADMIN")
    return <Alert color="red">최고 관리자만 환불 요청을 조회할 수 있습니다.</Alert>;
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(id))
    return <Alert color="red">환불 요청 번호를 확인해 주세요.</Alert>;
  return <RefundDetail key={`${actor.sub}:${id}`} owner={actor.sub} id={id} />;
}
const won = (amount: number) => `${amount.toLocaleString("ko-KR")}원`;
const date = (value: string) =>
  new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
function RefundDetail({ owner, id }: { owner: string; id: string }) {
  const detail = useQuery({
    queryKey: queryKeys.pro.adminRefundRequest(owner, id),
    queryFn: async () => {
      let changed = false;
      const unsubscribe = useAuthStore.subscribe((state) => {
        if (!state.token || state.user?.sub !== owner || state.user.role !== "SUPER_ADMIN")
          changed = true;
      });
      try {
        const result = await getProAdminRefundRequest(owner, id);
        const state = useAuthStore.getState();
        if (
          changed ||
          !state.token ||
          state.user?.sub !== owner ||
          state.user.role !== "SUPER_ADMIN"
        )
          throw new Error("계정이 변경되었습니다.");
        return readAdminRefundDetail(result, id);
      } finally {
        unsubscribe();
      }
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const data = detail.isError ? undefined : detail.data;
  return (
    <TitleContainer title="Pro 환불 요청 상세">
      <Stack>
        <Button component={Link} to="/pro-billing/refunds" variant="subtle">
          환불 목록으로
        </Button>
        <Text size="sm">
          문의 답변은 환불 승인이 아닙니다. 승인과 반려 시 검토 사유를 기록합니다. 실제 PG 환불
          처리는 준비 중입니다.
        </Text>
        <Button disabled={detail.isFetching} onClick={() => void detail.refetch()}>
          환불 상세 다시 조회
        </Button>
        {detail.isPending && <Text role="status">환불 상세를 불러오는 중입니다.</Text>}
        {detail.isError && (
          <Alert color="red" title="환불 상세 조회 실패">
            상세를 확인하지 못했습니다. 다시 조회해 주세요.
          </Alert>
        )}
        {data && (
          <>
            <Card withBorder>
              <Stack>
                <Title order={3}>접수 기록</Title>
                <Text>요청 번호: {data.request.id}</Text>
                <Text>상태: {statuses[data.request.status]}</Text>
                <Text>접수일: {date(data.request.requestedAt)}</Text>
                <Text>접수 당시 결제액: {won(data.request.paymentAmount)}</Text>
                <Text>접수 당시 잔액: {won(data.request.availableAmount)}</Text>
                <Text fw={600}>접수 사유</Text>
                <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {data.request.reason}
                </Text>
              </Stack>
            </Card>
            {data.confirmation && (
              <Card withBorder>
                <Stack>
                  <Title order={3}>저장된 환불 확정 기록</Title>
                  <Text>영수증 번호: {data.confirmation.id}</Text>
                  <Text>확정 환불액: {won(data.confirmation.amount)}</Text>
                  <Text>PG 환불 시각: {date(data.confirmation.refundedAt)}</Text>
                  <Text>서버 확정 시각: {date(data.confirmation.confirmedAt)}</Text>
                  <Text>이 결제의 이용권 회수가 확인되었습니다. 기존 근무표는 유지됩니다.</Text>
                </Stack>
              </Card>
            )}
            {["CONFIRMED", "REJECTED"].includes(data.request.status) && (
              <Card withBorder>
                <Stack>
                  <Title order={3}>환불 처리 결과 알림</Title>
                  {data.notification ? (
                    <>
                      <Text>알림 상태: {notificationStatuses[data.notification.status]}</Text>
                      <Text>처리 시도 기록: {data.notification.attempts}회</Text>
                      <Text>
                        확정 미접수: {data.notification.failures}회 · 결과 재조회:{" "}
                        {data.notification.uncertainLookups}회
                      </Text>
                      <Text>최종 기록: {date(data.notification.updatedAt)}</Text>
                      {data.notification.nextAttemptAt && (
                        <Text>다음 처리 예약: {date(data.notification.nextAttemptAt)}</Text>
                      )}
                      <Text size="sm" c="dimmed">
                        업체 접수는 고객의 메일 수신이나 읽음을 보장하지 않습니다. 시도 기록에는
                        발송 전 준비도 포함됩니다.
                      </Text>
                      {data.notification.status === "NEEDS_REVIEW" && (
                        <Alert color="yellow">
                          자동 알림 처리가 중단되었습니다. 접수 여부를 확인한 뒤 운영 절차에 따라
                          처리하세요.
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Text>
                      알림 처리 기록을 확인할 수 없습니다. 기록 부재만으로 발송 성공이나 실패를
                      판단하지 마세요.
                    </Text>
                  )}
                </Stack>
              </Card>
            )}
            {data.execution && (
              <Card withBorder>
                <Stack>
                  <Title order={3}>환불 실행 상태</Title>
                  <Text>진행 상태: {executionStatuses[data.execution.status]}</Text>
                  <Text>처리 시도: {data.execution.attempt}회 (전달·조회 포함)</Text>
                  <Text>최종 처리 기록: {date(data.execution.updatedAt)}</Text>
                  {data.execution.nextAttemptAt && (
                    <Text>다음 처리 예약: {date(data.execution.nextAttemptAt)}</Text>
                  )}
                  {data.execution.issue && (
                    <Text>
                      {data.execution.issue === "PROVIDER_FAILED"
                        ? "결제 모듈에서 환불 실패가 확인되었습니다."
                        : data.execution.issue === "UNCERTAIN"
                          ? "환불 결과가 아직 확정되지 않았습니다."
                          : "원결제와 환불 조회 기록의 대사가 필요합니다."}
                    </Text>
                  )}
                  {data.execution.status === "NEEDS_REVIEW" && (
                    <Alert color="yellow">
                      자동 처리가 중단되었습니다. 원결제와 환불 조회 증거를 확인한 뒤 운영 절차에
                      따라 처리하세요.
                    </Alert>
                  )}
                </Stack>
              </Card>
            )}
            {data.approval && !data.execution && (
              <Alert color="yellow">
                환불 실행 상태가 제공되지 않았습니다. 확정 영수증과 결제 기록을 함께 확인해 주세요.
              </Alert>
            )}
            {data.request.status === "CONFIRMED" && !data.confirmation && (
              <Alert color="yellow">
                환불 확정 영수증을 확인할 수 없습니다. 승인 기록만으로 환불 완료를 판단하지 마세요.
              </Alert>
            )}
            {data.approval && (
              <Card withBorder>
                <Stack>
                  <Title order={3}>저장된 승인 기록</Title>
                  <Text>결정 번호: {data.approval.id}</Text>
                  <Text>승인 시각: {date(data.approval.decidedAt)}</Text>
                  <Text>승인 당시 환불 예정액: {won(data.approval.amount)}</Text>
                  <Text>
                    승인 기록은 환불 완료를 뜻하지 않습니다. 실제 처리 상태는 요청 상태와 결제
                    기록에서 확인해 주세요.
                  </Text>
                  <Text fw={600}>검토 사유</Text>
                  <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {data.approval.reason}
                  </Text>
                </Stack>
              </Card>
            )}
            {["APPROVED", "PROCESSING", "CONFIRMED", "RETRY_REQUIRED"].includes(
              data.request.status,
            ) &&
              !data.approval && (
                <Alert color="yellow">저장된 승인 기록을 확인할 수 없습니다.</Alert>
              )}
            {data.rejection && (
              <Card withBorder>
                <Stack>
                  <Title order={3}>저장된 반려 기록</Title>
                  <Text>결정 번호: {data.rejection.id}</Text>
                  <Text>반려 시각: {date(data.rejection.decidedAt)}</Text>
                  <Text fw={600}>검토 사유</Text>
                  <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {data.rejection.reason}
                  </Text>
                </Stack>
              </Card>
            )}
            {data.request.status === "REJECTED" && !data.rejection && (
              <Alert color="yellow">저장된 반려 기록을 확인할 수 없습니다.</Alert>
            )}
            <Card withBorder>
              <Stack>
                <Title order={3}>현재 결제 기록</Title>
                <Text>주문 번호: {data.payment.orderId}</Text>
                <Text>결제 번호: {data.payment.id}</Text>
                <Text>결제일: {data.payment.paidAt ? date(data.payment.paidAt) : "미확정"}</Text>
                <Text>결제액: {won(data.payment.amount)}</Text>
                <Text>계약 기본 가격: {won(data.order.baseAmount)}</Text>
                <Text>할인율: {data.order.discountBps / 100}%</Text>
                <Text>결제 주기: {data.order.interval === "MONTH" ? "월간" : "연간"}</Text>
                <Text>
                  주문 지급 기간:{" "}
                  {data.order.periodStart && data.order.periodEnd
                    ? `${date(data.order.periodStart)} ~ ${date(data.order.periodEnd)}`
                    : "미확정"}
                </Text>
                {data.entitlement ? (
                  <>
                    <Text>
                      이용권 기간: {date(data.entitlement.startsAt)} ~{" "}
                      {date(data.entitlement.endsAt)}
                    </Text>
                    <Text>
                      이용권 회수:{" "}
                      {data.entitlement.revokedAt
                        ? date(data.entitlement.revokedAt)
                        : "회수되지 않음"}
                    </Text>
                  </>
                ) : (
                  <Text>해당 결제에 연결된 이용권이 없습니다.</Text>
                )}
                <Text>환불 누계: {won(data.payment.refundedAmount)}</Text>
                <Text>현재 미환불 잔액: {won(data.payment.availableAmount)}</Text>
                <Text size="sm">현재 잔액은 조회 시점의 값이며 승인된 환불 금액이 아닙니다.</Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack>
                <Title order={3}>관련 생성 작업</Title>
                {data.generation ? (
                  <>
                    <Text>작업 번호: {data.generation.id}</Text>
                    <Text>
                      작업 상태:{" "}
                      {
                        {
                          QUEUED: "대기 중",
                          RUNNING: "생성 중",
                          SUCCEEDED: "성공",
                          FAILED: "실패",
                          CANCELED: "취소",
                          EXPIRED: "시간 초과",
                        }[data.generation.status]
                      }
                    </Text>
                    <Text>접수 시각: {date(data.generation.createdAt)}</Text>
                    <Text>
                      종료 시각:{" "}
                      {data.generation.finishedAt ? date(data.generation.finishedAt) : "미종료"}
                    </Text>
                    <Text>오류 코드: {data.generation.errorCode ?? "없음"}</Text>
                    <Text>
                      생성 횟수:{" "}
                      {data.generation.usageState === "RELEASED"
                        ? "예약 반환됨"
                        : data.generation.usageState === "CONSUMED"
                          ? "차감됨"
                          : data.generation.usageState === "RESERVED"
                            ? "예약 중"
                            : "기록 없음"}
                    </Text>
                  </>
                ) : (
                  <Text>연결된 생성 작업을 조회할 수 없습니다.</Text>
                )}
                <Title order={3}>관련 문의</Title>
                {data.inquiry ? (
                  <>
                    <Text fw={600}>{data.inquiry.title}</Text>
                    <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {data.inquiry.content}
                    </Text>
                    <Text fw={600}>답변</Text>
                    <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {data.inquiry.answer ?? "아직 답변이 없습니다."}
                    </Text>
                    <Button
                      component={Link}
                      to={`/inquiries/${encodeURIComponent(data.inquiry.id)}`}
                      variant="light"
                    >
                      문의 확인
                    </Button>
                  </>
                ) : (
                  <Text>원본 문의를 조회할 수 없습니다. 접수 기록은 유지됩니다.</Text>
                )}
              </Stack>
            </Card>
          </>
        )}
        <RefundDecisionForm owner={owner} id={id} request={data?.request} />
      </Stack>
    </TitleContainer>
  );
}
